<script lang="ts">
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import { openAnchoredModal } from '#lib/menu/menu.ts';
	import SelectDropdown from '#lib/select/SelectDropdown.svelte';
	import {
		setSelectRegistryContext,
		type OptionMeta,
		type SelectValue
	} from '#lib/select/select-context.ts';
	import {
		formatDisplayLabel,
		nextSelectInstanceId,
		removeOption,
		upsertOption
	} from '#lib/select/select-logic.ts';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import type { Snippet } from 'svelte';
	import IconExpandMore from 'virtual:icons/material-symbols/expand-more-rounded';

	const DROPDOWN_MIN_WIDTH = 200;

	interface SelectedOption {
		value: SelectValue;
		label: string;
	}

	interface Props {
		value: SelectValue | SelectValue[] | null | undefined;
		children?: Snippet;
		footer?: Snippet;
		required?: boolean;
		disabled?: boolean;
		compact?: boolean;
		multiple?: boolean;
		onchange?: (value: any) => void;
		grow?: boolean;
		placeholder?: string;
		display?: Snippet<[SelectedOption[]]>;
		minWidth?: number;
	}
	let {
		disabled,
		children,
		footer,
		required,
		multiple = false,
		value = $bindable(),
		onchange,
		compact,
		grow,
		placeholder = t('common.notSelected'),
		display,
		minWidth = DROPDOWN_MIN_WIDTH
	}: Props = $props();

	const modalCtx = getModalContext();
	const instanceId = nextSelectInstanceId();

	let triggerEl: HTMLButtonElement | undefined = $state();
	let registeredOptions: OptionMeta[] = $state([]);
	let openModalId: string | null = $state(null);

	setSelectRegistryContext({
		register(meta) {
			registeredOptions = upsertOption(registeredOptions, meta);
		},
		unregister(unregisterValue) {
			registeredOptions = removeOption(registeredOptions, unregisterValue);
		}
	});

	const selectedValues = $derived(
		Array.isArray(value) ? value : value !== undefined && value !== null ? [value] : []
	);
	const selectedOptions = $derived(
		registeredOptions.filter((o) => selectedValues.includes(o.value))
	);
	const displayLabel = $derived(
		formatDisplayLabel(
			selectedOptions.map((o) => o.label),
			placeholder
		)
	);
	const isOpen = $derived(openModalId !== null);

	$effect(() => {
		if (openModalId && !modalCtx.modalHistory.some((m) => m.id === openModalId)) {
			openModalId = null;
			triggerEl?.focus();
		}
	});

	function handleSelect(v: SelectValue) {
		if (multiple) {
			const current = Array.isArray(value) ? value : [];
			value = current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
		} else {
			value = v;
		}
		onchange?.(value);
	}

	const OPTION_ROW_HEIGHT = 40;
	const OPTION_ROW_HEIGHT_WITH_DESCRIPTION = 62;
	const DROPDOWN_OVERHEAD = 8;
	// Must stay in sync with SelectDropdown.svelte's `.select-dropdown` max-height —
	// the dropdown scrolls internally past this, so the position estimate should
	// never exceed it either.
	const DROPDOWN_MAX_HEIGHT = 320;

	function openDropdown() {
		if (disabled || !triggerEl) {
			return;
		}
		const rowHeight = registeredOptions.some((o) => o.description)
			? OPTION_ROW_HEIGHT_WITH_DESCRIPTION
			: OPTION_ROW_HEIGHT;
		const estimatedHeight = Math.min(
			registeredOptions.length * rowHeight + DROPDOWN_OVERHEAD,
			DROPDOWN_MAX_HEIGHT
		);
		const width = Math.max(triggerEl.getBoundingClientRect().width, minWidth);

		openModalId = openAnchoredModal(
			modalCtx,
			triggerEl,
			SelectDropdown,
			{ children, footer, value, multiple, idPrefix: instanceId, onSelect: handleSelect },
			{ estimatedHeight, width, offset: { x: 0, y: DROPDOWN_OVERHEAD } }
		);
	}
</script>

<button
	bind:this={triggerEl}
	type="button"
	class="select-trigger"
	{disabled}
	aria-haspopup="listbox"
	aria-expanded={isOpen}
	onclick={openDropdown}
	class:compact
	class:grow
>
	<span class="select-value">
		{#if display}
			{@render display(selectedOptions)}
		{:else}
			{displayLabel}
		{/if}
	</span>
	<div class="icon" class:open={isOpen}>
		<IconExpandMore />
	</div>
</button>
<div class="select-registry" aria-hidden="true">
	{@render children?.()}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.select-trigger {
		grid-area: control;
		height: auto;
		background: var(--input-background);
		border: 1px solid var(--input-border);
		border-radius: 0.5rem;
		padding: 0.5rem;
		span.select-value {
			text-align: left;
			flex: 1 1 auto;
			font-size: 0.875rem;
			line-height: 0.875rem;
			color: var(--text-primary);
			@include lib.ellipsis;
		}
		&:hover {
			background: var(--input-background-hover);
		}
		&.grow {
			flex: 1 1 auto;
		}
		&.compact {
			padding: 0.25rem 0.25rem 0.25rem 0.5rem;

			.icon {
				right: 0.25rem;
			}
		}
	}

	.select-registry {
		position: absolute;
		width: 0;
		height: 0;
		overflow: hidden;
		pointer-events: none;
	}
</style>
